import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import moment from "moment";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import { PortfolioService } from "../../../portfolio/portfolio.service";
import { TreemapComponent, TreemapNode } from "../../charts/treemap.component";
import { HeatmapComponent } from "../../charts/heatmap.component";
import {
  AllocRiskBarsComponent,
  AllocRiskRow,
} from "../../../../charts/alloc-risk-bars/alloc-risk-bars.component";
import {
  HbarListComponent,
  HBarRow,
} from "../../../../charts/hbar-list/hbar-list.component";
import { ExpTimeAreaChartComponent } from "../../charts/time-area-chart.component";

@Component({
  selector: "app-exp-risk",
  standalone: true,
  imports: [
    TreemapComponent,
    HeatmapComponent,
    AllocRiskBarsComponent,
    HbarListComponent,
    ExpTimeAreaChartComponent,
  ],
  templateUrl: "./risk-experience.component.html",
})
export class RiskExperienceComponent implements OnInit, OnDestroy {
  portfolio: any = null;
  evaluationData: any = null;
  plotData: any = null;

  kpi = {
    expReturn: null as number | null,
    vol: null as number | null,
    sharpe: null as number | null,
    varPct: null as number | null,
  };
  treemapNodes: TreemapNode[] = [];
  allocRiskRows: AllocRiskRow[] = [];
  marginalRows: HBarRow[] = [];
  corrMatrix: { labels: string[]; matrix: number[][] } = {
    labels: [],
    matrix: [],
  };

  drawdownData: number[][] = [];
  rollingVolData: number[][] = [];

  topRisk: { ticker: string; contribution: number; weight: number } | null =
    null;

  isLoading = true;
  private subs = new Subscription();
  private portfolioId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private service: PortfolioService,
  ) {}

  ngOnInit(): void {
    this.subs.add(
      this.route.parent?.paramMap.subscribe((pm) => {
        const id = Number(pm.get("id"));
        if (id && id !== this.portfolioId) {
          this.portfolioId = id;
          this.load(id);
        }
      }) ?? Subscription.EMPTY,
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private load(id: number): void {
    this.isLoading = true;
    this.subs.add(
      forkJoin({
        portfolio: this.service.viewItem(id).pipe(catchError(() => of(null))),
        evaluation: this.service
          .fetchEvaluationData(id)
          .pipe(catchError(() => of(null))),
        plot: this.service
          .fetchPerformancePlot(id)
          .pipe(catchError(() => of(null))),
        correlation: this.service
          .getCorrelationMatrix(id)
          .pipe(catchError(() => of(null))),
        performance: this.service
          .getEvaluationData(id, "baseline_portfolio")
          .pipe(catchError(() => of(null))),
      }).subscribe(
        ({ portfolio, evaluation, plot, correlation, performance }) => {
          this.portfolio = portfolio;
          this.evaluationData = evaluation;
          this.plotData = plot;
          this.setupKpis();
          this.setupRisk();
          this.setupCorrelation(correlation);
          this.processEvaluationPerformance(performance);
          this.isLoading = false;
        },
      ),
    );
  }

  /** Mirrors evaluation.component.ts -> processEvaluationPerformanceResponse for drawdown/rolling vol time series. */
  private processEvaluationPerformance(resp: any): void {
    if (!Array.isArray(resp)) {
      this.drawdownData = [];
      this.rollingVolData = [];
      return;
    }
    const drawdown: number[][] = [];
    const volatility: number[][] = [];
    for (const data of resp) {
      const value = +(data.value * 100).toFixed(2);
      const date = moment(data.date).unix() * 1000;
      if (!isFinite(value) || !isFinite(date)) continue;
      if (data.variable === "drawdown") {
        drawdown.push([date, value]);
      } else if (data.variable === "rolling_std_deviation" && value > 0) {
        volatility.push([date, value]);
      }
    }
    this.drawdownData = drawdown;
    this.rollingVolData = volatility;
  }

  private setupKpis(): void {
    const summary = this.portfolio?.performance_summary || {};
    this.kpi.expReturn = summary["Annualized Return"]?.Portfolio ?? null;
    this.kpi.vol = summary["Annualized Std Dev"]?.Portfolio ?? null;
    this.kpi.sharpe =
      summary["Sharpe Ratio"]?.Portfolio ??
      summary["Sharpe"]?.Portfolio ??
      null;
    const var95 =
      summary["VaR 95"]?.Portfolio ??
      summary["VaR95"]?.Portfolio ??
      summary["Value at Risk"]?.Portfolio;
    this.kpi.varPct = typeof var95 === "number" ? var95 : null;
  }

  private setupRisk(): void {
    const symbols: any[] = this.portfolio?.symbol_performance || [];
    const rc: any[] = this.evaluationData?.risk_contribution || [];
    const byTicker = new Map<string, number>();
    for (const r of rc) {
      if (r.Ticker === "Portfolio") continue;
      if (r.Portfolio !== "baseline_portfolio" && r.Portfolio !== undefined)
        continue;
      byTicker.set(r.Ticker, Number(r.risk_contribution) || 0);
    }
    const allocRows: AllocRiskRow[] = [];
    const nodes: TreemapNode[] = [];
    const marginal: HBarRow[] = [];
    let top: { ticker: string; contribution: number; weight: number } | null =
      null;

    for (const s of symbols) {
      if (s.Ticker === "Portfolio" || !(s.Weight > 0)) continue;
      const contribution = byTicker.get(s.Ticker) ?? 0;
      allocRows.push({
        label: s.Ticker,
        allocation: s.Weight,
        risk: contribution,
      });
      nodes.push({ label: s.Ticker, value: Math.max(contribution, 0.0001) });
      marginal.push({ label: s.Ticker, value: (s.Weight || 0) * contribution });
      if (!top || contribution > top.contribution) {
        top = { ticker: s.Ticker, contribution, weight: s.Weight };
      }
    }

    allocRows.sort((a, b) => b.risk - a.risk);
    nodes.sort((a, b) => b.value - a.value);
    marginal.sort((a, b) => b.value - a.value);
    this.allocRiskRows = allocRows.slice(0, 10);
    this.treemapNodes = nodes.slice(0, 12);
    this.marginalRows = marginal.slice(0, 8);
    this.topRisk = top;
  }

  private setupCorrelation(raw: any): void {
    if (!raw) {
      this.corrMatrix = { labels: [], matrix: [] };
      return;
    }

    const items: any[] | null = Array.isArray(raw?.items)
      ? raw.items
      : Array.isArray(raw) && raw.length && "Var1" in raw[0] && "Var2" in raw[0]
        ? raw
        : null;
    if (items && items.length) {
      const labelOrder: string[] = [];
      const seen = new Set<string>();
      for (const it of items) {
        const a = String(it.Var1 ?? "");
        const b = String(it.Var2 ?? "");
        if (a && !seen.has(a)) {
          seen.add(a);
          labelOrder.push(a);
        }
        if (b && !seen.has(b)) {
          seen.add(b);
          labelOrder.push(b);
        }
      }
      const labels = labelOrder.slice(0, Math.min(10, labelOrder.length));
      const idx = new Map(labels.map((l, i) => [l, i]));
      const matrix: number[][] = labels.map(() => labels.map(() => 0));
      for (const it of items) {
        const i = idx.get(String(it.Var1 ?? ""));
        const j = idx.get(String(it.Var2 ?? ""));
        if (i === undefined || j === undefined) continue;
        const v = Number(it.value);
        if (!isFinite(v)) continue;
        matrix[i][j] = v;
        matrix[j][i] = v;
      }
      this.corrMatrix = { labels, matrix };
      return;
    }

    if (Array.isArray(raw) && raw.length) {
      const labels = Object.keys(raw[0])
        .filter((k) => k !== "Ticker" && k !== "Row" && k !== "index")
        .slice(0, 10);
      const matrix = raw
        .slice(0, labels.length)
        .map((row: any) => labels.map((c) => Number(row[c]) || 0));
      this.corrMatrix = { labels, matrix };
      return;
    }
    if (raw.labels && raw.matrix) {
      const n = Math.min(10, raw.labels.length);
      this.corrMatrix = {
        labels: raw.labels.slice(0, n),
        matrix: raw.matrix.slice(0, n).map((r: number[]) => r.slice(0, n)),
      };
      return;
    }
    if (typeof raw === "object") {
      const labels = Object.keys(raw)
        .filter((k) => typeof raw[k] === "object")
        .slice(0, 10);
      const matrix = labels.map((r) =>
        labels.map((c) => Number(raw[r]?.[c]) || 0),
      );
      this.corrMatrix = { labels, matrix };
    }
  }

  fmtPct(v: number | null | undefined, d = 1): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return (v * 100).toFixed(d) + "%";
  }
  fmtNum(v: number | null | undefined, d = 2): string {
    if (v === undefined || v === null || !isFinite(v)) return "—";
    return v.toFixed(d);
  }
}
